import currency from "currency.js";

import { MONTHS_IN_QUARTER, VintageHalfQuarter } from "@/app/lib/date-helpers";
import { formatAttributes, ReturnTypeFormatAttributes } from "@/app/lib/format-attributes";
import { RECOrderAttributeOptions } from "@/app/lib/format-rec-options";
import { getTotalValue, reverseFeePercentageCalculation } from "@/app/lib/order-helpers";
import {
  CommodityGenerationSummary,
  OrderStatus,
  RecAttributes,
  SpotTradeTransactionSummaryQuery,
} from "@/app/types/generated/graphql";

import {
  TransactionSummaryData,
  TransactionSummaryTransactionContractProductAttributes,
} from "../pdf-components/documents/transaction-summary/transaction-summary.types";

export const generateVintagesFromCommodityGenerationSummary = (
  commodityGenerationSummary: CommodityGenerationSummary[],
): string[] => {
  return commodityGenerationSummary.map(({ date }) => {
    if (!date) return "";
    const [year, month] = date.split("-");
    return `${month}-${year}`;
  });
};

export const generateVintagesFromFormattedVintages = (
  formattedVintages: ReturnTypeFormatAttributes["vintages"],
): string[] => {
  return formattedVintages.map((vintage) => {
    const [_, quarter] = vintage.split("-");
    const [startOfQuarter, __, endOfQuarter] = MONTHS_IN_QUARTER[quarter.trim() as VintageHalfQuarter];
    return `${vintage} (${startOfQuarter} - ${endOfQuarter})`;
  });
};

export const spotTradeTransactionSummaryDataMapper = (
  data: SpotTradeTransactionSummaryQuery,
  recOptions: RECOrderAttributeOptions | undefined,
  isActiveRegistryErcot: boolean,
): TransactionSummaryData | undefined => {
  const {
    spotTradeTransactionSummary: { order, tradeTransactions },
  } = data;

  if (!order) {
    return;
  }

  const formattedAttributes = formatAttributes({
    attributes: order?.attributes as RecAttributes,
    position: order?.position,
    options: recOptions,
  });

  const transactions = tradeTransactions.map((transaction) => {
    const total = currency(transaction.unitPrice).multiply(transaction.volume).value;
    const feePercentage = reverseFeePercentageCalculation(transaction.transactionFee, total);

    const productAttributes = transaction.transactionDetails?.map(
      (detail): TransactionSummaryTransactionContractProductAttributes => {
        const { trackingSystem, attributes, commodityGenerationSummary, generator } = detail;

        const formattedAttributes = formatAttributes({
          attributes: attributes as RecAttributes,
          position: order?.position,
          options: recOptions,
        });

        const vintages = isActiveRegistryErcot
          ? generateVintagesFromFormattedVintages(formattedAttributes.vintages)
          : generateVintagesFromCommodityGenerationSummary(commodityGenerationSummary ?? []);

        return {
          eligibilities: formattedAttributes.eligibilities,
          fuelSources: formattedAttributes.fuelSources,
          trackingSystem,
          projects: isActiveRegistryErcot ? formattedAttributes.projects : generator?.name ? [generator.name] : [],
          locations: formattedAttributes.locations,
          vintages,
          certificateTypes: formattedAttributes.certificateTypes,
          certificationScheme: {
            certifications: formattedAttributes.certifications,
            crsEffectiveDate: generator?.crsEffectiveDate ?? "",
            crsExpiryDate: generator?.crsExpiryDate ?? "",
            commencementDate: generator?.commencementDate ?? "",
          },
        };
      },
    );

    return {
      contractId: transaction.transactionNumber ?? "",
      marketplace: "TraceX USA",
      participantName: order.account?.company?.businessName ?? "",
      position: transaction.position,
      matchedDate: transaction.matchedDate,
      completedDate: transaction.completedDate,
      price: transaction.unitPrice,
      volume: transaction.volume,
      totalContractValue: getTotalValue(transaction.unitPrice, transaction.volume, order.position, feePercentage / 100)
        .value,
      transactionFeeAmount: transaction.transactionFee,
      transactionFeePercentage: feePercentage,
      productAttributes,
    };
  });

  const totalOrderValue =
    order.status === OrderStatus.Withdrawn && transactions.length === 0
      ? currency(order.price.amount).multiply(order.initialVolume).value
      : transactions.reduce((acc, transaction) => currency(acc).add(transaction.totalContractValue).value, 0);

  return {
    order: {
      orderNumber: order?.orderNumber ?? "",
      orderDate: order.createdOn ?? "",
      marketplace: "TraceX USA",
      participantName: order.account?.company?.businessName ?? "",
      position: order.position,
      type: order.type,
      volume: order.initialVolume,
      price: +order.price.amount,
      contractIDs: transactions.map(({ contractId }) => contractId).filter(Boolean),
      assetType: order.assetType,
      totalOrderValue,
      currencyCode: order.price.currencyCode,
      attributes: {
        eligibilities: formattedAttributes.eligibilities,
        fuelSources: formattedAttributes.fuelSources,
        locations: formattedAttributes.locations,
        vintages: formattedAttributes.vintages,
        certifications: formattedAttributes.certifications,
        certificateTypes: formattedAttributes.certificateTypes,
      },
      status: order.status,
    },
    transactions,
  };
};
